import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useSubscription } from '@apollo/client';
import { useOutletContext } from 'react-router-dom';
import { GenerationStatus, type GenerationKind } from '@courseai/domain';
import type { ProjectTabContext } from '../ProjectDetail';
import { useT, type I18n, type TranslationKey } from '../../i18n';
import {
  MY_GENERATION_REQUESTS,
  ON_GENERATION_REQUEST_UPDATED,
  type GenerationRequestView,
  type MyGenerationRequestsData,
  type MyGenerationRequestsVariables,
  type OnGenerationRequestUpdatedData,
  type OnGenerationRequestUpdatedVariables,
} from '../../graphql/generations';

// Translation keys for each generation status shown in the status badge (Req 6.1).
const STATUS_KEYS: Record<GenerationStatus, TranslationKey> = {
  PENDING: 'generationStatus.PENDING',
  RUNNING: 'generationStatus.RUNNING',
  READY: 'generationStatus.READY',
  FAILED: 'generationStatus.FAILED',
};

// Translation keys describing what kind of work a request performs.
const KIND_KEYS: Record<GenerationKind, TranslationKey> = {
  AGENT_COURSE: 'generationKind.AGENT_COURSE',
  EPISODE_AUDIO: 'generationKind.EPISODE_AUDIO',
  EPISODE_VIDEO: 'generationKind.EPISODE_VIDEO',
};

function kindLabel(t: I18n['t'], kind: string): string {
  const key = KIND_KEYS[kind as GenerationKind];
  return key === undefined ? kind : t(key);
}

function statusLabel(t: I18n['t'], status: string): string {
  const key = STATUS_KEYS[status as GenerationStatus];
  return key === undefined ? status : t(key);
}

// Generaciones tab: a live-updating list of the project's asynchronous
// generation jobs. It subscribes to `onGenerationRequestUpdated(projectId)` and
// renders each request with its kind and status; FAILED requests also show the
// recorded error description (Req 6.1, 6.6).
//
// A subscription delivers only CHANGES, so there is no historical snapshot to
// seed from — the schema offers a single-item `getGenerationRequest` but no
// list query. The view therefore accumulates updates in component state keyed
// by generationRequestId, updating each request in place as new payloads
// arrive. Seeding the initial historical list would require a list query added
// server-side later (see graphql/generations.ts for the same note).
export function GenerationsTab(): ReactNode {
  const { projectId } = useOutletContext<ProjectTabContext>();
  const { t } = useT();

  // The latest known state of every request seen on this subscription, keyed by
  // generationRequestId so repeated updates for the same request replace the
  // previous state rather than appending.
  const [requestsById, setRequestsById] = useState<ReadonlyMap<string, GenerationRequestView>>(
    () => new Map(),
  );

  // Reset the accumulated list when switching projects so we never show another
  // project's requests while the new subscription warms up.
  useEffect(() => {
    setRequestsById(new Map());
  }, [projectId]);

  // Seed the view with the project's EXISTING generations (newest first) so the
  // tab shows historical jobs, not only live subscription changes. The
  // subscription below then layers real-time updates on top.
  const {
    data: listData,
    loading: listLoading,
    error: listError,
    refetch,
  } = useQuery<MyGenerationRequestsData, MyGenerationRequestsVariables>(MY_GENERATION_REQUESTS, {
    variables: { projectId },
    skip: projectId === '',
    fetchPolicy: 'cache-and-network',
  });

  // Merge the seed list into the accumulated map (without clobbering any newer
  // live update already applied for the same request id).
  useEffect(() => {
    const seeded = listData?.myGenerationRequests;
    if (seeded === undefined) {
      return;
    }
    setRequestsById((prev) => {
      const next = new Map(prev);
      for (const request of seeded) {
        const existing = next.get(request.generationRequestId);
        // Prefer whichever is newer by updatedAt so a live update isn't
        // overwritten by a slightly stale seed (and vice-versa).
        if (existing === undefined || existing.updatedAt <= request.updatedAt) {
          next.set(request.generationRequestId, request);
        }
      }
      return next;
    });
  }, [listData]);

  const { error: subscriptionError } = useSubscription<
    OnGenerationRequestUpdatedData,
    OnGenerationRequestUpdatedVariables
  >(ON_GENERATION_REQUEST_UPDATED, {
    variables: { projectId },
    skip: projectId === '',
    onData: ({ data }) => {
      const update = data.data?.onGenerationRequestUpdated;
      if (!update) {
        return;
      }
      setRequestsById((prev) => {
        const next = new Map(prev);
        next.set(update.generationRequestId, update);
        return next;
      });
    },
  });

  // Newest activity first: order by updatedAt descending (ISO timestamps sort
  // lexicographically), then by created request id for stability.
  const requests = useMemo(() => {
    return [...requestsById.values()].sort((a, b) => {
      if (a.updatedAt === b.updatedAt) {
        return a.generationRequestId.localeCompare(b.generationRequestId);
      }
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
  }, [requestsById]);

  return (
    <div className="tab-panel">
      <div className="tab-panel-header">
        <h3>{t('generations.title')}</h3>
        <button type="button" onClick={() => void refetch()}>
          {t('generations.refresh')}
        </button>
      </div>

      {listError && (
        <p className="form-error">{t('generations.loadError', { message: listError.message })}</p>
      )}
      {subscriptionError && (
        <p className="form-error">
          {t('generations.subscribeError', { message: subscriptionError.message })}
        </p>
      )}

      {listLoading && requests.length === 0 ? (
        <p className="tab-placeholder">{t('generations.loading')}</p>
      ) : requests.length === 0 ? (
        <p className="tab-placeholder">{t('generations.empty')}</p>
      ) : (
        <ul className="generation-list">
          {requests.map((request) => (
            <GenerationRow key={request.generationRequestId} request={request} />
          ))}
        </ul>
      )}
    </div>
  );
}

interface GenerationRowProps {
  request: GenerationRequestView;
}

// A single generation request: its kind, an optional episode hint, a live
// status badge, and — only when FAILED — the recorded error description
// (Req 6.6). The badge colour is driven by a status modifier class.
function GenerationRow({ request }: GenerationRowProps): ReactNode {
  const { t } = useT();
  const { kind, status, episodeIndex, errorDescription } = request;
  const isFailed = status === GenerationStatus.FAILED;

  return (
    <li className="generation-row">
      <div className="generation-row-main">
        <span className="generation-row-kind">
          {kindLabel(t, kind)}
          {typeof episodeIndex === 'number' && (
            <span className="generation-row-episode">
              {t('generations.episode', { index: episodeIndex + 1 })}
            </span>
          )}
        </span>
        <span
          className={`generation-status generation-status-${status.toLowerCase()}`}
          role="status"
        >
          {statusLabel(t, status)}
        </span>
      </div>
      {isFailed && (
        <p className="generation-row-error">
          {errorDescription ?? t('generations.failedNoDescription')}
        </p>
      )}
    </li>
  );
}
